/**
 * Wuva Purchase Options Widget V3.5
 * =================================
 * Self-contained module that renders purchase/subscription/rental options
 * inside the Shopify Horizon theme product page.
 *
 * V3.5 changes from V3.4:
 * - Adds _client_id and _merchant line item properties to all checkout paths
 * - _client_id sourced from the wuva.clientId product metafield (config.clientId)
 * - _merchant hardcoded to "Wuva"
 * - Extracted _setFormLineItemProperty helper to DRY up form-input sync
 *
 * V3.4 changes from V3.3:
 * - Adds _revenue_model line item property to all checkout paths
 * - Purchase tab → _revenue_model: "purchase"
 * - Subscribe tab (non-hire) → _revenue_model: "subscription"  
 * - Lease tab (hire types) → _revenue_model: "lease"
 * - Rent tab → _revenue_model: "rental"
 * - Property is underscore-prefixed so it's hidden from customer-facing views
 * - Visible in Shopify admin, API, and Matrixify exports
 *
 * V3.3 changes:
 * - Default VAT relief ON for eligible products (majority of customers use VAT relief)
 * - Hide accelerated checkout on VAT-relief-eligible products
 *
 * V3.2 fix:
 * - Variant swap moved from submit-time to post-ATC cart swap
 * - Form submits the original variant normally (Horizon handles selling plans correctly)
 * - After successful ATC, line item is swapped to VAT relief pair in the cart
 * - Eliminates all form interception issues with Horizon and accelerated checkout
 *
 * V3.1 fix:
 * - Cart attributes are debounced (1s after last keystroke) to avoid 429 rate limiting
 * - Flushed immediately before ATC to ensure attributes are written
 * - Only VAT-eligible products touch cart attributes
 *
 * Architecture (unchanged):
 * - Liquid serializes product data as JSON (single source of truth)
 * - JS handles ALL rendering, pricing, and state
 * - Purchase & Subscription: injects selling_plan into existing form
 * - Rental: separate ATC via fetch (multi-line-item bundle)
 * - Hooks into Horizon's ThemeEvents for variant changes
 * - Custom range calendar lazy-built on first rental tab open
 */
(function () {
  'use strict';

  /* ========================================================================
     1. CONSTANTS
     ======================================================================== */
  const VAT_RATE = 1.2;
  const WUVA_API_BASE = 'https://wuva-api-prod.wuva.com/api/v1/brand-referrer';
  const MAX_RENTAL_DAYS = 89;
  const VAT_FIELD_MAX = 100;
  const MERCHANT_NAME = 'Wuva';
  const CAL_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const CAL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }
  function fmtDateLabel(d) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  function daysBetween(a, b) {
    return Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
  }

  /* ========================================================================
     2. SVG ICONS (inline strings)
     ======================================================================== */
  const ICONS = {
    shield: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M8 1.5L3 3.5v4c0 3.5 2.5 5.5 5 6.5 2.5-1 5-3 5-6.5v-4L8 1.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M5.5 8l2 2 3-3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    truck: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M1 3h9v8H1zM10 6h2.5L14.5 9v2H10V6z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><circle cx="4" cy="12" r="1.3" stroke="currentColor" stroke-width="1.2"/><circle cx="12" cy="12" r="1.3" stroke="currentColor" stroke-width="1.2"/></svg>',
    refresh: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M2 8a6 6 0 0110.5-4M14 8a6 6 0 01-10.5 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M12.5 1v3h-3M3.5 15v-3h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    box: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M2 5l6-3 6 3v6l-6 3-6-3V5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M2 5l6 3m0 0l6-3M8 8v6.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
    checkCircle: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 8l2 2 3-3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    calendar: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M2 6.5h12" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
    user: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="currentColor" stroke-width="1.3"/><path d="M2.5 14c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
    file: '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M4 1.5h5l4 4v9H4V1.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M9 1.5v4h4" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>',
    lock: '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3.5" y="7" width="9" height="7" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
  };

  function icon(name) {
    return `<span class="wuva-icon">${ICONS[name] || ''}</span>`;
  }

  /* ========================================================================
     3. UTILITIES
     ======================================================================== */

  function formatMoney(cents, currency, locale) {
    return new Intl.NumberFormat(locale || 'en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
    }).format(cents / 100);
  }

  function applyVat(priceInCents, isVatRelief) {
    if (!isVatRelief) return priceInCents;
    return Math.round(priceInCents / VAT_RATE);
  }

  function applyVatPounds(pounds, isVatRelief) {
    if (!isVatRelief) return pounds;
    return pounds / VAT_RATE;
  }

  function parsePlanDuration(name) {
    return name;
  }

  function el(tag, attrs, children) {
    const e = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === 'className') e.className = v;
        else if (k === 'textContent') e.textContent = v;
        else if (k === 'innerHTML') e.innerHTML = v;
        else if (k.startsWith('on') && typeof v === 'function') e.addEventListener(k.slice(2).toLowerCase(), v);
        else if (k === 'style' && typeof v === 'object') Object.assign(e.style, v);
        else if (v !== null && v !== undefined && v !== false) e.setAttribute(k, v);
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(c => {
        if (c == null) return;
        e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      });
    }
    return e;
  }

  /* ========================================================================
     4. WIDGET CLASS
     ======================================================================== */
  class WuvaWidget {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.container = document.getElementById(`wuva2-widget-${sectionId}`);
      const configEl = document.getElementById(`wuva2-config-${sectionId}`);
      if (!this.container || !configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[Wuva] Failed to parse config:', e);
        return;
      }

      this.state = {
        currentTab: null,
        selectedPlanId: null,
        vatRelief: false,
        vatFormValid: false,
        vatFormTouched: { name: false, condition: false },
        rentalPricing: null,
        rentalConstraints: null,
        minRentalDays: null,
        rentalDates: { start: null, end: null },
        rentalQuickDays: 0,
        calendarLoaded: false,
        calendarInstance: null,
      };

      // V3.1: Track the latest cart-attribute write as a resolved promise
      // so the submit handler can await it without blocking when nothing is pending.
      this._cartAttrPromise = Promise.resolve();
      this._syncDebounceTimer = null;

      this.variant = this._findVariant(this.config.selectedVariantId);
      if (!this.variant) this.variant = this.config.variants[0];

      this.planPrices = {};
      this._recalcPlanPrices();

      // V3.3: Default VAT relief ON for eligible products
      this.state.vatRelief = !!this.variant?.vatReliefEligible;
      this._abortCtrl = new AbortController();
      this.refs = {};

      this._render();
      this._bindEvents();
      this._updateSubscribeTabVisibility();
      this._checkRentalAvailability();

      // Default tab: Subscribe first, then Buy, then Rental
      if (this._variantHasPlans()) {
        this._switchTab('subscription');
      } else if (this.config.allowsOneTimePurchase) {
        this._switchTab('purchase');
      }

      // V3: Initial checkout blocking state
      this._updateCheckoutBlocked();

      // V3.3: Hide accelerated checkout on VAT-relief-eligible products
      // (Shop Pay / Google Pay etc. bypass our variant swap)
      if (this.config.variants.some(v => v.vatReliefEligible)) {
        const formComp = this._getProductForm();
        const accel = formComp?.querySelector('.accelerated-checkout-block');
        if (accel) accel.hidden = true;
      }
    }

    /* ======================================================================
       VARIANT HELPERS
       ====================================================================== */

    _findVariant(id) {
      return this.config.variants.find(v => v.id == id) || null;
    }

    /** Extract numeric variant ID from a metafield reference (object, GID string, or number) */
    _parsePairId(val) {
      if (!val) return null;
      if (typeof val === 'object' && val !== null) val = val.id || val.admin_graphql_api_id || val;
      val = String(val);
      const match = val.match(/(\d+)$/);
      return match ? parseInt(match[1]) : null;
    }

    _recalcPlanPrices() {
      this.planPrices = {};
      if (!this.variant) return;
      const allocs = this.variant.sellingPlanAllocations || [];
      allocs.forEach(plan => {
        this.planPrices[plan.id] = plan.price;
      });
    }

    _allPlans() {
      return this.variant?.sellingPlanAllocations || [];
    }

    _variantHasPlans() {
      return (this.variant?.sellingPlanAllocations?.length || 0) > 0;
    }

    /* ======================================================================
       V3.4: REVENUE MODEL HELPER
       ====================================================================== */

    /**
     * Returns the revenue model string for the current tab.
     * - purchase tab → "purchase"
     * - subscription tab + hire type → "lease"
     * - subscription tab + non-hire type → "subscription"
     * - rental tab → "rental"
     */
    _getRevenueModel() {
      switch (this.state.currentTab) {
        case 'purchase':
          return 'purchase';
        case 'subscription':
          // subscribeLabel is "Lease" for hire types, "Subscribe" for others
          return (this.config.subscribeLabel || '').toLowerCase() === 'lease' ? 'lease' : 'subscription';
        case 'rental':
          return 'rental';
        default:
          return 'purchase';
      }
    }

    /* ======================================================================
       PRICING
       ====================================================================== */

    _fmt(cents) {
      return formatMoney(cents, this.config.shopCurrency, this.config.shopLocale);
    }

    _adjustedPrice(cents) {
      return applyVat(cents, this.state.vatRelief);
    }

    _adjustedPricePounds(pounds) {
      return applyVatPounds(pounds, this.state.vatRelief);
    }

    _fmtPounds(pounds) {
      return `£${this._adjustedPricePounds(pounds).toFixed(2)}`;
    }

    /* ======================================================================
       RENDER
       ====================================================================== */

    _render() {
      const frag = document.createDocumentFragment();

      // Tab navigation
      frag.appendChild(this._renderTabs());

      // Panels
      if (this.config.allowsOneTimePurchase) {
        frag.appendChild(this._renderBuyPanel());
      }
      if (this.config.hasSellingPlans) {
        frag.appendChild(this._renderSubscriptionPanel());
      }
      frag.appendChild(this._renderRentalPanel());

      // V3: VAT Relief section (banner + form) — below panels
      if (this.variant?.vatReliefEligible) {
        frag.appendChild(this._renderVatSection());
      }

      // Rental ATC button — below VAT section so user fills form then clicks ATC
      if (this.refs.rentalAtcBtn) {
        frag.appendChild(this.refs.rentalAtcBtn);
      }

      // Error message
      const errEl = el('div', { className: 'wuva-error', 'data-visible': 'false' });
      frag.appendChild(errEl);
      this.refs.error = errEl;

      this.container.innerHTML = '';
      this.container.appendChild(frag);
    }

    /* ── VAT Relief Section (banner + declaration form) ── */

    _renderVatSection() {
      const section = el('div', { className: 'wuva-vat-section' });

      // Banner
      section.appendChild(this._renderVatBanner());

      // Declaration form (animated)
      const formWrap = el('div', {
        className: 'wuva-vat-form',
        'data-open': this.state.vatRelief ? 'true' : 'false',
      });

      const formInner = el('div', { className: 'wuva-vat-form-inner' });

      formInner.appendChild(el('div', {
        className: 'wuva-vat-form-intro',
        textContent: 'To claim VAT relief, confirm you have a qualifying disability or long-term condition.',
      }));

      // Name field
      const nameField = el('div', { className: 'wuva-vat-field' });
      const nameLabel = el('label', { className: 'wuva-vat-label' });
      nameLabel.innerHTML = `${icon('user')} Full name of eligible person`;
      nameField.appendChild(nameLabel);

      const nameInput = el('input', {
        className: 'wuva-vat-input',
        type: 'text',
        placeholder: 'Enter full name',
        maxlength: String(VAT_FIELD_MAX),
        'data-error': 'false',
      });
      nameInput.addEventListener('input', () => this._onVatFieldChange());
      nameInput.addEventListener('blur', () => {
        this.state.vatFormTouched.name = true;
        this._validateVatField('name');
      });
      nameInput.addEventListener('focus', () => { nameInput.dataset.error = 'false'; });
      nameField.appendChild(nameInput);

      const nameFooter = el('div', { className: 'wuva-vat-field-footer' });
      const nameError = el('span', { className: 'wuva-vat-field-error', style: { display: 'none' }, textContent: 'Required' });
      const nameCount = el('span', { className: 'wuva-vat-field-count', textContent: `0/${VAT_FIELD_MAX}` });
      nameFooter.append(nameError, nameCount);
      nameField.appendChild(nameFooter);
      formInner.appendChild(nameField);

      // Condition field
      const condField = el('div', { className: 'wuva-vat-field' });
      const condLabel = el('label', { className: 'wuva-vat-label' });
      condLabel.innerHTML = `${icon('file')} Qualifying condition`;
      condField.appendChild(condLabel);

      const condInput = el('textarea', {
        className: 'wuva-vat-input wuva-vat-textarea',
        placeholder: 'Describe the disability or long-term condition',
        maxlength: String(VAT_FIELD_MAX),
        rows: '2',
        'data-error': 'false',
      });
      condInput.addEventListener('input', () => this._onVatFieldChange());
      condInput.addEventListener('blur', () => {
        this.state.vatFormTouched.condition = true;
        this._validateVatField('condition');
      });
      condInput.addEventListener('focus', () => { condInput.dataset.error = 'false'; });
      condField.appendChild(condInput);

      const condFooter = el('div', { className: 'wuva-vat-field-footer' });
      const condError = el('span', { className: 'wuva-vat-field-error', style: { display: 'none' }, textContent: 'Required' });
      const condCount = el('span', { className: 'wuva-vat-field-count', textContent: `0/${VAT_FIELD_MAX}` });
      condFooter.append(condError, condCount);
      condField.appendChild(condFooter);
      formInner.appendChild(condField);

      // Status line
      const status = el('div', {
        className: 'wuva-vat-status',
        'data-complete': 'false',
      });
      status.innerHTML = `${icon('lock')} Declaration stored securely with your order`;
      formInner.appendChild(status);

      formWrap.appendChild(formInner);
      section.appendChild(formWrap);

      // Checkout blocked message (below form, above buttons)
      const blockedMsg = el('div', {
        className: 'wuva-checkout-blocked-msg',
        'data-visible': this.state.vatRelief ? 'true' : 'false',
      });
      blockedMsg.innerHTML = `${icon('shield')} Fill in the VAT relief form to checkout`;
      section.appendChild(blockedMsg);

      this.refs.vatSection = section;
      this.refs.vatFormWrap = formWrap;
      this.refs.vatNameInput = nameInput;
      this.refs.vatCondInput = condInput;
      this.refs.vatNameError = nameError;
      this.refs.vatCondError = condError;
      this.refs.vatNameCount = nameCount;
      this.refs.vatCondCount = condCount;
      this.refs.vatStatus = status;
      this.refs.vatBlockedMsg = blockedMsg;

      return section;
    }

    _renderVatBanner() {
      const banner = el('div', {
        className: 'wuva-vat-banner',
        'data-active': this.state.vatRelief ? 'true' : 'false',
      });

      const left = el('div', { className: 'wuva-vat-banner-left' });
      left.appendChild(el('span', { className: 'wuva-icon', innerHTML: ICONS.shield, style: { color: '#235c4d' } }));
      left.appendChild(el('span', { className: 'wuva-vat-banner-label', textContent: 'VAT Relief eligible' }));

      const saving = el('span', { className: 'wuva-vat-saving' });
      let initPrice = this.variant.price;
      if (this.state.currentTab === 'subscription' && this.state.selectedPlanId && this.planPrices[this.state.selectedPlanId] !== undefined) {
        initPrice = this.planPrices[this.state.selectedPlanId];
      }
      const savingAmount = initPrice - Math.round(initPrice / VAT_RATE);
      saving.textContent = `Saving ${this._fmt(savingAmount)}`;
      left.appendChild(saving);
      this.refs.vatSaving = saving;

      banner.appendChild(left);

      // Toggle
      const toggle = el('button', { type: 'button',
        className: 'wuva-toggle',
        'data-on': this.state.vatRelief ? 'true' : 'false',
        'aria-label': 'Toggle VAT Relief',
      });
      toggle.appendChild(el('span', { className: 'wuva-toggle-knob' }));
      toggle.addEventListener('click', () => this._toggleVatRelief());
      banner.appendChild(toggle);

      this.refs.vatBanner = banner;
      this.refs.vatToggle = toggle;
      return banner;
    }

    /* ── VAT Relief Logic ── */

    _toggleVatRelief() {
      this.state.vatRelief = !this.state.vatRelief;
      const on = this.state.vatRelief;

      // Update toggle + banner
      this.refs.vatToggle.dataset.on = on ? 'true' : 'false';
      this.refs.vatBanner.dataset.active = on ? 'true' : 'false';

      // Show/hide form
      if (this.refs.vatFormWrap) {
        this.refs.vatFormWrap.dataset.open = on ? 'true' : 'false';
      }

      // Clear form if toggling off
      if (!on) {
        if (this.refs.vatNameInput) this.refs.vatNameInput.value = '';
        if (this.refs.vatCondInput) this.refs.vatCondInput.value = '';
        this.state.vatFormTouched = { name: false, condition: false };
        this.state.vatFormValid = false;
        if (this.refs.vatNameError) this.refs.vatNameError.style.display = 'none';
        if (this.refs.vatCondError) this.refs.vatCondError.style.display = 'none';
        if (this.refs.vatNameInput) this.refs.vatNameInput.dataset.error = 'false';
        if (this.refs.vatCondInput) this.refs.vatCondInput.dataset.error = 'false';
        if (this.refs.vatNameCount) this.refs.vatNameCount.textContent = `0/${VAT_FIELD_MAX}`;
        if (this.refs.vatCondCount) this.refs.vatCondCount.textContent = `0/${VAT_FIELD_MAX}`;
        this._updateVatStatus();
      }

      this._updateVatSaving();
      this._updateAllPrices();
      this._updateCheckoutBlocked();

      // V3.1: Sync cart attributes immediately on toggle (not debounced)
      this._syncCartAttributes(true);
    }

    /* ── V3.1: Unified cart attribute sync (debounced) ── */
    _syncCartAttributes(immediate = false) {
      if (!this.variant?.vatReliefEligible) {
        this._cartAttrPromise = Promise.resolve();
        return;
      }

      if (this._syncDebounceTimer) {
        clearTimeout(this._syncDebounceTimer);
        this._syncDebounceTimer = null;
      }

      const doSync = () => {
        if (this.state.vatRelief && this.state.vatFormValid) {
          this._cartAttrPromise = this._setCartAttributes();
        } else {
          this._cartAttrPromise = this._clearCartAttributes();
        }
      };

      if (immediate) {
        doSync();
      } else {
        this._syncDebounceTimer = setTimeout(doSync, 1000);
      }
    }

    async _clearCartAttributes() {
      const body = JSON.stringify({
        attributes: {
          'VAT Relief Claimed': '',
          'VAT Relief Claim Type': '',
          'VAT Relief Name': '',
          'VAT Relief Disability': '',
          'VAT Relief Timestamp': '',
        },
      });
      for (let attempt = 0; attempt <= 2; attempt++) {
        try {
          const resp = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
          if (resp.status === 429) {
            await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
            continue;
          }
          return;
        } catch (e) {
          console.error('[Wuva] Failed to clear cart attributes:', e);
          return;
        }
      }
    }

    _onVatFieldChange() {
      const name = (this.refs.vatNameInput?.value || '').trim();
      const cond = (this.refs.vatCondInput?.value || '').trim();

      if (this.refs.vatNameCount) this.refs.vatNameCount.textContent = `${this.refs.vatNameInput?.value?.length || 0}/${VAT_FIELD_MAX}`;
      if (this.refs.vatCondCount) this.refs.vatCondCount.textContent = `${this.refs.vatCondInput?.value?.length || 0}/${VAT_FIELD_MAX}`;

      this.state.vatFormValid = name.length > 0 && cond.length > 0;
      this._updateVatStatus();
      this._updateCheckoutBlocked();

      this._syncCartAttributes();
    }

    _validateVatField(field) {
      if (field === 'name' && this.state.vatFormTouched.name) {
        const empty = !(this.refs.vatNameInput?.value || '').trim();
        if (this.refs.vatNameError) this.refs.vatNameError.style.display = empty ? '' : 'none';
        if (this.refs.vatNameInput) this.refs.vatNameInput.dataset.error = empty ? 'true' : 'false';
      }
      if (field === 'condition' && this.state.vatFormTouched.condition) {
        const empty = !(this.refs.vatCondInput?.value || '').trim();
        if (this.refs.vatCondError) this.refs.vatCondError.style.display = empty ? '' : 'none';
        if (this.refs.vatCondInput) this.refs.vatCondInput.dataset.error = empty ? 'true' : 'false';
      }
    }

    _updateVatStatus() {
      if (!this.refs.vatStatus) return;
      if (this.state.vatFormValid) {
        this.refs.vatStatus.dataset.complete = 'true';
        this.refs.vatStatus.innerHTML = `${icon('checkCircle')} Declaration complete`;
      } else {
        this.refs.vatStatus.dataset.complete = 'false';
        this.refs.vatStatus.innerHTML = `${icon('lock')} Declaration stored securely with your order`;
      }
    }

    _updateVatSaving() {
      if (!this.refs.vatSaving || !this.variant) return;

      if (this.state.currentTab === 'rental') {
        const { start, end } = this.state.rentalDates;
        if (!start || !end || !this.state.rentalPricing) {
          this.refs.vatSaving.textContent = '';
          return;
        }
        const days = Math.ceil((end - start) / 86400000) + 1;
        if (days <= 0) { this.refs.vatSaving.textContent = ''; return; }
        const rp = this.state.rentalPricing;
        const totalPounds = rp.fixedCosts + (rp.dayRate * days);
        const savingPounds = totalPounds - (totalPounds / VAT_RATE);
        this.refs.vatSaving.textContent = `Saving £${savingPounds.toFixed(2)}`;
        return;
      }

      let activePrice = this.variant.price;
      if (this.state.currentTab === 'subscription' && this.state.selectedPlanId && this.planPrices[this.state.selectedPlanId] !== undefined) {
        activePrice = this.planPrices[this.state.selectedPlanId];
      }
      const savingAmount = activePrice - Math.round(activePrice / VAT_RATE);
      const suffix = this.state.currentTab === 'subscription' ? '/mo' : '';
      this.refs.vatSaving.textContent = `Saving ${this._fmt(savingAmount)}${suffix}`;
    }

    /* ── Checkout Blocking ── */

    _isCheckoutBlocked() {
      return this.state.vatRelief && !this.state.vatFormValid;
    }

    _updateCheckoutBlocked() {
      const blocked = this._isCheckoutBlocked();

      if (blocked) {
        document.body.classList.add('wuva-checkout-blocked');
      } else {
        document.body.classList.remove('wuva-checkout-blocked');
      }

      if (this.refs.vatBlockedMsg) {
        this.refs.vatBlockedMsg.dataset.visible = blocked && this.state.currentTab !== 'rental' ? 'true' : 'false';
      }

      this._updateRentalButtonState();
    }

    /* ── Variant Swap + Cart Attributes (V3.2) ── */

    /**
     * V3.5: Set or remove a `properties[<key>]` hidden input on the product form.
     * Pass null/undefined/'' for value to remove the input.
     */
    _setFormLineItemProperty(form, key, value) {
      const name = `properties[${key}]`;
      const existing = form.querySelector(`input[name="${name}"]`);

      if (value === null || value === undefined || value === '') {
        if (existing) existing.remove();
        return;
      }

      if (existing) {
        existing.value = value;
      } else {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }
    }

    /**
     * V3.5: Sync selling plan AND line item properties into the form.
     * Runs on every tab switch and plan change so the form always
     * has the correct hidden inputs before Horizon reads them.
     *
     * Line item properties synced:
     *   _revenue_model — purchase / subscription / lease (see _getRevenueModel)
     *   _client_id     — from wuva.clientId product metafield (config.clientId)
     *   _merchant      — always "Wuva"
     *
     * On the rental tab these properties are removed from the form because
     * the rental flow writes them directly via _handleRentalAddToCart().
     */
    _syncSellingPlanToForm() {
      const formComponent = this._getProductForm();
      if (!formComponent) return;
      const form = formComponent.querySelector('form');
      if (!form) return;

      // ── Selling plan input ──
      const existingSP = form.querySelector('input[name="selling_plan"]');

      if (this.state.currentTab === 'subscription' && this.state.selectedPlanId) {
        if (existingSP) {
          existingSP.value = this.state.selectedPlanId;
        } else {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.value = this.state.selectedPlanId;
          form.appendChild(input);
        }
      } else {
        if (existingSP) existingSP.remove();
      }

      // ── Line item properties ──
      const isRental = this.state.currentTab === 'rental';
      this._setFormLineItemProperty(form, '_revenue_model', isRental ? null : this._getRevenueModel());
      this._setFormLineItemProperty(form, '_client_id',     isRental ? null : (this.config.clientId || null));
      this._setFormLineItemProperty(form, '_merchant',      isRental ? null : MERCHANT_NAME);
    }

    async _setCartAttributes() {
      if (!this.state.vatRelief || !this.state.vatFormValid) return;

      const name = (this.refs.vatNameInput?.value || '').trim();
      const cond = (this.refs.vatCondInput?.value || '').trim();

      const attributes = {
        'VAT Relief Claimed': 'true',
        'VAT Relief Claim Type': 'individual',
        'VAT Relief Name': name,
        'VAT Relief Disability': cond,
        'VAT Relief Timestamp': new Date().toISOString(),
      };

      const body = JSON.stringify({ attributes });
      for (let attempt = 0; attempt <= 2; attempt++) {
        try {
          const resp = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
          if (resp.status === 429) {
            await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
            continue;
          }
          if (!resp.ok) {
            console.error('[Wuva] Cart attribute update failed:', resp.status);
          }
          return;
        } catch (e) {
          console.error('[Wuva] Failed to set cart attributes:', e);
          return;
        }
      }
    }

    /**
     * V3.3: Override the theme's handleSubmit for VAT relief.
     */
    _interceptFormSubmit() {
      const formComponent = this._getProductForm();
      if (!formComponent) return;
      const form = formComponent.querySelector('form');
      if (!form || form.dataset.wuvaIntercepted) return;

      form.dataset.wuvaIntercepted = 'true';

      const origHandleSubmit = formComponent.handleSubmit.bind(formComponent);

      formComponent.handleSubmit = async (event) => {
        event.preventDefault();

        if (this._isCheckoutBlocked()) {
          this.state.vatFormTouched = { name: true, condition: true };
          this._validateVatField('name');
          this._validateVatField('condition');
          if (this.refs.vatSection) {
            this.refs.vatSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }

        // Flush any pending debounced cart attribute write before ATC
        if (this._syncDebounceTimer) {
          clearTimeout(this._syncDebounceTimer);
          this._syncDebounceTimer = null;
          if (this.state.vatRelief && this.state.vatFormValid) {
            this._cartAttrPromise = this._setCartAttributes();
          }
        }
        await this._cartAttrPromise;

        // VAT RELIEF ACTIVE: swap the variant ID
        const variantInput = form.querySelector('input[name="id"]');
        const originalId = variantInput?.value;
        const parsedPairId = this._parsePairId(this.variant?.vatReliefPairId);

        if (this.state.vatRelief && this.state.vatFormValid && parsedPairId && variantInput) {
          variantInput.value = parsedPairId;
        }

        origHandleSubmit(event);

        if (originalId && variantInput) {
          setTimeout(() => { variantInput.value = originalId; }, 200);
        }
      };
    }

    /* ── Tab Navigation (underline) ── */

    _renderTabs() {
      const nav = el('div', { className: 'wuva-tabs', role: 'tablist', 'data-count': '0' });
      this.refs.tabNav = nav;

      // Subscribe/Lease tab
      if (this.config.hasSellingPlans) {
        const subBtn = el('button', { type: 'button',
          className: 'wuva-tab-btn', role: 'tab',
          'aria-selected': 'false', 'data-tab': 'subscription',
          textContent: this.config.subscribeLabel || 'Subscribe',
        });
        subBtn.addEventListener('click', () => this._switchTab('subscription'));
        nav.appendChild(subBtn);
        this.refs.subTabBtn = subBtn;
      }

      // Rent tab (hidden until API confirms)
      const rentBtn = el('button', { type: 'button',
        className: 'wuva-tab-btn', role: 'tab',
        'aria-selected': 'false', 'data-tab': 'rental',
        textContent: 'Rent',
        style: { display: 'none' },
      });
      rentBtn.addEventListener('click', () => this._switchTab('rental'));
      nav.appendChild(rentBtn);
      this.refs.rentTabBtn = rentBtn;

      // Buy tab
      if (this.config.allowsOneTimePurchase) {
        const buyBtn = el('button', { type: 'button',
          className: 'wuva-tab-btn', role: 'tab',
          'aria-selected': 'false', 'data-tab': 'purchase',
          textContent: 'Buy',
        });
        buyBtn.addEventListener('click', () => this._switchTab('purchase'));
        nav.appendChild(buyBtn);
        this.refs.buyTabBtn = buyBtn;
      }

      this._updateTabCount();
      return nav;
    }

    /* ── Buy Panel ── */

    _renderBuyPanel() {
      const panel = el('div', { className: 'wuva-panel', 'data-panel': 'purchase', 'data-active': 'false', role: 'tabpanel' });

      const priceWrap = el('div', { className: 'wuva-buy-price' });
      const priceVal = el('div', { className: 'wuva-buy-price-value', textContent: this._fmt(this._adjustedPrice(this.variant?.price || 0)) });
      priceWrap.appendChild(priceVal);
      panel.appendChild(priceWrap);
      this.refs.buyPrice = priceVal;

      const benefits = el('div', { className: 'wuva-benefits-inline' });
      [
        { ico: 'truck', text: 'Free delivery' },
        { ico: 'checkCircle', text: '2-year warranty' },
        { ico: 'box', text: 'Yours to keep' },
      ].forEach(b => {
        const item = el('div', { className: 'wuva-benefit-item' });
        item.innerHTML = `${icon(b.ico)} ${b.text}`;
        benefits.appendChild(item);
      });
      panel.appendChild(benefits);

      if (this.config.hasSellingPlans) {
        const plans = this._allPlans();
        let lowestPrice = Infinity;
        plans.forEach(p => {
          const pp = this.planPrices[p.id] || this.variant?.price || 0;
          if (pp < lowestPrice) lowestPrice = pp;
        });

        const nudge = el('div', { className: 'wuva-nudge-card' });
        const nudgeLeft = el('div');
        const nudgeVerb = (this.config.subscribeLabel || 'Subscribe').toLowerCase();
        const nudgeText = el('div', { className: 'wuva-nudge-text', textContent: `Or ${nudgeVerb} from ${this._fmt(this._adjustedPrice(lowestPrice))}/mo` });
        const nudgeSub = el('div', { className: 'wuva-nudge-sub', textContent: 'Includes insurance, free delivery & returns' });
        nudgeLeft.append(nudgeText, nudgeSub);
        nudge.appendChild(nudgeLeft);

        const nudgeBtn = el('button', { type: 'button', className: 'wuva-nudge-btn', textContent: 'View plans' });
        nudgeBtn.addEventListener('click', () => this._switchTab('subscription'));
        nudge.appendChild(nudgeBtn);

        panel.appendChild(nudge);
        this.refs.nudgeText = nudgeText;
      }

      this.refs.buyPanel = panel;
      return panel;
    }

    /* ── Subscribe Panel ── */

    _renderSubscriptionPanel() {
      const panel = el('div', { className: 'wuva-panel', 'data-panel': 'subscription', 'data-active': 'false', role: 'tabpanel' });

      const grid = el('div', { className: 'wuva-plans', 'data-count': '0' });
      panel.appendChild(grid);
      this.refs.planGrid = grid;

      const summary = el('div', { className: 'wuva-breakdown' });
      const termRow = el('div', { className: 'wuva-breakdown-row' }, [
        el('span', { textContent: 'Minimum term' }),
        el('span', { textContent: '—' }),
      ]);
      const priceRow = el('div', { className: 'wuva-breakdown-row' }, [
        el('span', { textContent: 'Monthly price' }),
        el('span', { textContent: '—' }),
      ]);
      summary.append(termRow, priceRow);
      panel.appendChild(summary);

      const benefits = el('div', { className: 'wuva-benefits-inline' });
      [
        { ico: 'truck', text: 'Free delivery & returns' },
        { ico: 'refresh', text: 'Cancel anytime after your minimum term' },
        { ico: 'shield', text: 'Wear & tear cover' },
      ].forEach(b => {
        const item = el('div', { className: 'wuva-benefit-item' });
        item.innerHTML = `${icon(b.ico)} ${b.text}`;
        benefits.appendChild(item);
      });
      panel.appendChild(benefits);

      this.refs.summaryTerm = termRow.lastElementChild;
      this.refs.summaryPrice = priceRow.lastElementChild;
      this.refs.subscriptionPanel = panel;

      this._populatePlanGrid();

      return panel;
    }

    _populatePlanGrid() {
      if (!this.refs.planGrid) return;

      const plans = this._allPlans();
      const count = Math.min(plans.length, 4);

      this.refs.planGrid.innerHTML = '';
      this.refs.planGrid.dataset.count = String(count);

      if (count === 0) {
        this.state.selectedPlanId = null;
        this._syncSellingPlanToForm();
        return;
      }

      let highestPrice = 0;
      plans.forEach(p => {
        const pp = this.planPrices[p.id] || 0;
        if (pp > highestPrice) highestPrice = pp;
      });

      plans.slice(0, 4).forEach((plan, i) => {
        const pp = this.planPrices[plan.id] || 0;
        const isLast = i === count - 1;

        const btn = el('button', { type: 'button',
          className: 'wuva-plan-btn', role: 'radio',
          'aria-checked': isLast ? 'true' : 'false',
          'data-plan-id': String(plan.id),
        });

        if (isLast && count > 1) {
          btn.appendChild(el('span', { className: 'wuva-plan-badge', textContent: 'Best value' }));
        }

        btn.appendChild(el('div', { className: 'wuva-plan-duration', textContent: parsePlanDuration(plan.name) }));
        const priceDiv = el('div', { className: 'wuva-plan-price' });
        priceDiv.innerHTML = `${this._fmt(this._adjustedPrice(pp))}<span class="wuva-plan-per">/mo</span>`;
        btn.appendChild(priceDiv);

        let savingsText = 'Most flexible';
        if (pp !== highestPrice && highestPrice > 0) {
          const pct = Math.round(((highestPrice - pp) / highestPrice) * 100);
          savingsText = pct > 0 ? `Save ${pct}%` : 'Best value';
        }
        btn.appendChild(el('div', { className: 'wuva-plan-savings', textContent: savingsText }));

        btn.addEventListener('click', () => this._selectPlan(plan.id));
        this.refs.planGrid.appendChild(btn);
      });

      const defaultPlan = plans[Math.min(plans.length - 1, 3)];
      this._selectPlan(defaultPlan.id);
    }

    /* ── Rental Panel ── */

    _renderRentalPanel() {
      const panel = el('div', { className: 'wuva-panel', 'data-panel': 'rental', 'data-active': 'false', role: 'tabpanel' });

      const dateRow = el('div', { className: 'wuva-date-row' });

      const startGroup = el('div', { className: 'wuva-date-group' });
      startGroup.appendChild(el('label', { className: 'wuva-date-label', textContent: 'Delivery date' }));
      const startInput = el('input', { className: 'wuva-date-input', type: 'text', placeholder: 'Select date', readonly: '' });
      startGroup.appendChild(startInput);

      const endGroup = el('div', { className: 'wuva-date-group' });
      endGroup.appendChild(el('label', { className: 'wuva-date-label', textContent: 'Collection date' }));
      const endInput = el('input', { className: 'wuva-date-input', type: 'text', placeholder: 'Select date', readonly: '' });
      endGroup.appendChild(endInput);

      dateRow.append(startGroup, endGroup);
      panel.appendChild(dateRow);

      const bd = el('div', { className: 'wuva-breakdown', style: { display: 'none' } });
      const periodRow = el('div', { className: 'wuva-breakdown-row' }, [
        el('span', { textContent: 'Rental period' }),
        el('span', { textContent: '0 days' }),
      ]);
      const rateRow = el('div', { className: 'wuva-breakdown-row' }, [
        el('span', { textContent: 'Daily rate' }),
        el('span', { textContent: '£0/day' }),
      ]);
      const totalRow = el('div', { className: 'wuva-breakdown-total' }, [
        el('span', { textContent: 'Total' }),
        el('span', { textContent: '£0.00' }),
      ]);
      bd.append(periodRow, rateRow, totalRow);
      panel.appendChild(bd);

      const benefits = el('div', { className: 'wuva-benefits-inline' });
      [
        { ico: 'truck', text: 'Free delivery' },
        { ico: 'refresh', text: 'Free collection' },
        { ico: 'shield', text: 'Insured' },
      ].forEach(b => {
        const item = el('div', { className: 'wuva-benefit-item' });
        item.innerHTML = `${icon(b.ico)} ${b.text}`;
        benefits.appendChild(item);
      });
      panel.appendChild(benefits);

      const atcBtn = el('button', { type: 'button',
        className: 'wuva-rental-atc',
        textContent: 'Select dates to rent',
        disabled: '',
        style: { display: 'none' },
      });
      atcBtn.addEventListener('click', () => this._handleRentalAddToCart());

      this.refs.startInput = startInput;
      this.refs.endInput = endInput;
      this.refs.rentalBreakdown = bd;
      this.refs.rentalPeriod = periodRow.lastElementChild;
      this.refs.dailyRate = rateRow.lastElementChild;
      this.refs.rentalTotal = totalRow.lastElementChild;
      this.refs.rentalAtcBtn = atcBtn;
      this.refs.rentalPanel = panel;

      const openCal = (e) => {
        e.preventDefault();
        if (this.state.calendarInstance) this.state.calendarInstance.open();
      };
      startInput.addEventListener('click', openCal);
      startInput.addEventListener('focus', openCal);
      endInput.addEventListener('click', openCal);
      endInput.addEventListener('focus', openCal);

      return panel;
    }

    /* ======================================================================
       TAB MANAGEMENT
       ====================================================================== */

    _switchTab(tab) {
      this.state.currentTab = tab;

      if (tab === 'rental') {
        document.body.classList.add('wuva-rental-active');
      } else {
        document.body.classList.remove('wuva-rental-active');
      }

      this.container.querySelectorAll('.wuva-tab-btn').forEach(btn => {
        btn.setAttribute('aria-selected', btn.dataset.tab === tab ? 'true' : 'false');
      });

      this.container.querySelectorAll('.wuva-panel').forEach(p => {
        p.dataset.active = p.dataset.panel === tab ? 'true' : 'false';
      });

      if (tab === 'rental' && !this.state.calendarLoaded) {
        this._loadCalendar();
      }

      if (this.refs.rentalAtcBtn) {
        this.refs.rentalAtcBtn.style.display = tab === 'rental' ? '' : 'none';
      }

      // V3.5: Sync selling plan + line item properties into form on tab change
      this._syncSellingPlanToForm();

      this._updateVatSaving();
    }

    _updateTabCount() {
      if (!this.refs.tabNav) return;
      const visible = Array.from(this.refs.tabNav.querySelectorAll('.wuva-tab-btn'))
        .filter(b => b.style.display !== 'none');
      this.refs.tabNav.dataset.count = String(visible.length);
    }

    _updateSubscribeTabVisibility() {
      if (!this.refs.subTabBtn) return;
      const hasPlans = this._variantHasPlans();
      this.refs.subTabBtn.style.display = hasPlans ? '' : 'none';
      if (this.refs.subscriptionPanel) {
        this.refs.subscriptionPanel.style.display = hasPlans ? '' : 'none';
      }
      this._updateTabCount();
      if (!hasPlans && this.state.currentTab === 'subscription') {
        if (this.config.allowsOneTimePurchase) this._switchTab('purchase');
        else if (this.state.rentalPricing) this._switchTab('rental');
      }
    }

    /* ======================================================================
       PLAN SELECTION
       ====================================================================== */

    _selectPlan(planId) {
      this.state.selectedPlanId = planId;

      this.container.querySelectorAll('.wuva-plan-btn').forEach(btn => {
        btn.setAttribute('aria-checked', btn.dataset.planId == planId ? 'true' : 'false');
      });

      const pp = this.planPrices[planId];
      const plan = this._allPlans().find(p => p.id == planId);

      if (this.refs.summaryTerm && plan) {
        this.refs.summaryTerm.textContent = parsePlanDuration(plan.name);
      }
      if (this.refs.summaryPrice && pp !== undefined) {
        this.refs.summaryPrice.textContent = `${this._fmt(this._adjustedPrice(pp))}/mo`;
      }

      // V3.5: Sync selling plan + line item properties into form on plan change
      this._syncSellingPlanToForm();

      this._updateVatSaving();
    }

    /* ======================================================================
       QUICK-PICK DURATION (Rental)
       ====================================================================== */

    _selectQuickDuration(days) {
      this.state.rentalQuickDays = days;

      if (this.refs.durationRow) {
        this.refs.durationRow.querySelectorAll('.wuva-duration-btn').forEach(btn => {
          btn.setAttribute('aria-checked', btn.dataset.days == days ? 'true' : 'false');
        });
      }

      if (this.state.calendarInstance) {
        const minDate = this.state.rentalConstraints?.startDate
          ? new Date(this.state.rentalConstraints.startDate)
          : new Date();
        const start = new Date(minDate);
        if (start <= new Date()) {
          start.setDate(new Date().getDate() + 1);
        }
        const end = new Date(start);
        end.setDate(end.getDate() + days - 1);

        this.state.calendarInstance.setDate([start, end], true);
      } else {
        const start = new Date();
        start.setDate(start.getDate() + 1);
        const end = new Date(start);
        end.setDate(end.getDate() + days - 1);
        this.state.rentalDates = { start, end };

        const opts = { day: 'numeric', month: 'short' };
        if (this.refs.startInput) this.refs.startInput.value = start.toLocaleDateString('en-GB', opts);
        if (this.refs.endInput) this.refs.endInput.value = end.toLocaleDateString('en-GB', opts);

        this._calcRentalPricing();
        this._updateRentalButtonState();
      }
    }

    /* ======================================================================
       FORM INTEGRATION
       ====================================================================== */

    _getProductForm() {
      return document.querySelector(
        `product-form-component[data-section-id="${this.sectionId}"][data-product-id="${this.config.productId}"]`
      ) || document.querySelector(
        `product-form-component[data-product-id="${this.config.productId}"]`
      );
    }

    /* ======================================================================
       PRICE UPDATES
       ====================================================================== */

    _updateAllPrices() {
      if (this.refs.buyPrice) {
        this.refs.buyPrice.textContent = this._fmt(this._adjustedPrice(this.variant?.price || 0));
      }

      const plans = this._allPlans();
      let lowestPrice = Infinity;
      let highestPrice = 0;
      plans.forEach(p => {
        const pp = this.planPrices[p.id] || 0;
        if (pp < lowestPrice) lowestPrice = pp;
        if (pp > highestPrice) highestPrice = pp;
      });

      if (this.refs.nudgeText && lowestPrice < Infinity) {
        const nv = (this.config.subscribeLabel || 'Subscribe').toLowerCase();
        this.refs.nudgeText.textContent = `Or ${nv} from ${this._fmt(this._adjustedPrice(lowestPrice))}/mo`;
      }

      this.container.querySelectorAll('.wuva-plan-btn').forEach(btn => {
        const pid = btn.dataset.planId;
        const pp = this.planPrices[pid];
        if (pp === undefined) return;

        const priceEl = btn.querySelector('.wuva-plan-price');
        if (priceEl) priceEl.innerHTML = `${this._fmt(this._adjustedPrice(pp))}<span class="wuva-plan-per">/mo</span>`;

        const savingsEl = btn.querySelector('.wuva-plan-savings');
        if (savingsEl) {
          if (pp === highestPrice) {
            savingsEl.textContent = 'Most flexible';
          } else if (highestPrice > 0) {
            const pct = Math.round(((highestPrice - pp) / highestPrice) * 100);
            savingsEl.textContent = pct > 0 ? `Save ${pct}%` : 'Best value';
          }
        }
      });

      if (this.refs.summaryPrice && this.state.selectedPlanId) {
        const pp = this.planPrices[this.state.selectedPlanId];
        if (pp !== undefined) {
          this.refs.summaryPrice.textContent = `${this._fmt(this._adjustedPrice(pp))}/mo`;
        }
      }

      this._updateVatSaving();
      this._calcRentalPricing();
    }

    /* ======================================================================
       RENTAL: API CALLS
       ====================================================================== */

    async _checkRentalAvailability() {
      const clientId = (this.config.clientId || '').toUpperCase();
      const extId = this.variant?.externalVariantId;
      if (!clientId || !extId) {
        this._hideRentTab();
        return;
      }

      this._abortCtrl.abort();
      this._abortCtrl = new AbortController();

      try {
        const url = `${WUVA_API_BASE}/product-variant/${clientId}/?externalVariantId=${extId}&api-version=1`;
        const resp = await fetch(url, {
          signal: this._abortCtrl.signal,
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await resp.json();

        if (data.isSuccess && data.value?.isCalendarRentingEnabled) {
          this.state.rentalPricing = {
            dayRate: data.value.dayRate || 0,
            fixedCosts: data.value.fixedCosts || 0,
            lowestDailyCost: data.value.lowestDailyCost || 0,
            hireFeeInternalId: data.value.hireFeeInternalId,
            dayRateInternalId: data.value.dayRateInternalId,
            vatFreeHireFeeInternalId: data.value.vatFreeHireFeeInternalId || null,
            vatFreeDayRateInternalId: data.value.vatFreeDayRateInternalId || null,
          };
          this._showRentTab();

          if (data.value.id) {
            await this._fetchRentalConstraints(clientId, data.value.id);
          }
        } else {
          this._hideRentTab();
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[Wuva] Rental check failed:', e);
          this._hideRentTab();
        }
      }
    }

    async _fetchRentalConstraints(clientId, productVariantId) {
      try {
        const url = `${WUVA_API_BASE}/product-variant/minimum-calendar-rental-dates/?productVariantId=${productVariantId.toUpperCase()}&api-version=1`;
        const resp = await fetch(url, {
          signal: this._abortCtrl.signal,
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await resp.json();

        if (data.isSuccess && data.value) {
          this.state.rentalConstraints = data.value;
          this.state.minRentalDays = data.value.minimumRentalPeriodInDays;
          this._applyCalendarConstraints();
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[Wuva] Rental constraints failed:', e);
        }
      }
    }

    _showRentTab() {
      if (this.refs.rentTabBtn) this.refs.rentTabBtn.style.display = '';
      this._updateTabCount();
    }

    _hideRentTab() {
      if (this.refs.rentTabBtn) this.refs.rentTabBtn.style.display = 'none';
      this.state.rentalPricing = null;
      this._updateTabCount();
      if (this.state.currentTab === 'rental') {
        if (this.config.allowsOneTimePurchase) this._switchTab('purchase');
        else if (this.config.hasSellingPlans) this._switchTab('subscription');
      }
    }

    /* ======================================================================
       RENTAL: CALENDAR
       Self-contained range picker rendered with CSS Grid so day cells
       always map 1:1 to weekday columns.
       ====================================================================== */

    _loadCalendar() {
      if (this.state.calendarLoaded) return;
      this.state.calendarLoaded = true;
      this._initCalendar();
    }

    _initCalendar() {
      if (!this.refs.startInput) return;

      const widget = this;
      const today = startOfDay(new Date());
      const initMin = this.state.rentalConstraints?.startDate
        ? startOfDay(new Date(this.state.rentalConstraints.startDate))
        : today;

      const cal = {
        viewYear: initMin.getFullYear(),
        viewMonth: initMin.getMonth(),
        startDate: null,
        endDate: null,
        minDate: initMin,
        hoverDate: null,
        isOpen: false,
      };

      const container = el('div', { className: 'wuva-cal', 'data-open': 'false', role: 'dialog' });
      // Keep clicks inside the calendar from reaching the document-level
      // outside-click handler (re-rendering detaches the click target).
      container.addEventListener('mousedown', (e) => e.stopPropagation());
      container.addEventListener('click', (e) => e.stopPropagation());
      document.body.appendChild(container);
      cal.container = container;

      const sameDay = (a, b) => a && b && a.getTime() === b.getTime();

      const isDisabled = (cellDate) => {
        if (cellDate < cal.minDate) return true;
        // Only block days that exceed the maximum span from the chosen start.
        // Days shorter than minRentalDays stay clickable; we clamp on click.
        if (cal.startDate && !cal.endDate) {
          const diff = daysBetween(cal.startDate, cellDate) + 1;
          if (diff > MAX_RENTAL_DAYS) return true;
        }
        return false;
      };

      const buildMonth = (month, year, isFirst, isLast) => {
        const wrap = el('div', { className: 'wuva-cal-month' });

        const header = el('div', { className: 'wuva-cal-header' });

        const prev = el('button', {
          type: 'button',
          className: 'wuva-cal-nav',
          'aria-label': 'Previous month',
        });
        prev.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12 6 8l4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        if (!isFirst) prev.style.visibility = 'hidden';
        prev.addEventListener('click', () => {
          cal.viewMonth -= 1;
          if (cal.viewMonth < 0) { cal.viewMonth = 11; cal.viewYear -= 1; }
          render();
        });
        header.appendChild(prev);

        header.appendChild(el('div', {
          className: 'wuva-cal-title',
          textContent: `${CAL_MONTHS[month]} ${year}`,
        }));

        const next = el('button', {
          type: 'button',
          className: 'wuva-cal-nav',
          'aria-label': 'Next month',
        });
        next.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        if (!isLast) next.style.visibility = 'hidden';
        next.addEventListener('click', () => {
          cal.viewMonth += 1;
          if (cal.viewMonth > 11) { cal.viewMonth = 0; cal.viewYear += 1; }
          render();
        });
        header.appendChild(next);

        wrap.appendChild(header);

        const wd = el('div', { className: 'wuva-cal-weekdays' });
        CAL_WEEKDAYS.forEach(d => {
          wd.appendChild(el('div', { className: 'wuva-cal-weekday', textContent: d }));
        });
        wrap.appendChild(wd);

        const grid = el('div', { className: 'wuva-cal-days' });
        const firstWeekday = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        for (let i = 0; i < firstWeekday; i++) {
          grid.appendChild(el('div', { className: 'wuva-cal-blank' }));
        }

        for (let d = 1; d <= daysInMonth; d++) {
          const cellDate = startOfDay(new Date(year, month, d));
          const classes = ['wuva-cal-day'];
          const disabled = isDisabled(cellDate);

          if (disabled) classes.push('wuva-cal-disabled');
          if (sameDay(cellDate, today)) classes.push('wuva-cal-today');
          if (sameDay(cellDate, cal.startDate)) classes.push('wuva-cal-start');
          if (sameDay(cellDate, cal.endDate)) classes.push('wuva-cal-end');
          if (cal.startDate && cal.endDate && cellDate > cal.startDate && cellDate < cal.endDate) {
            classes.push('wuva-cal-inrange');
          }
          if (cal.startDate && !cal.endDate && cal.hoverDate && cal.hoverDate > cal.startDate) {
            if (cellDate > cal.startDate && cellDate < cal.hoverDate) classes.push('wuva-cal-inrange');
            if (sameDay(cellDate, cal.hoverDate) && !disabled) classes.push('wuva-cal-hover-end');
          }

          const cell = el('button', {
            type: 'button',
            className: classes.join(' '),
            textContent: String(d),
          });
          if (disabled) cell.disabled = true;

          cell.addEventListener('click', () => {
            if (cell.disabled) return;
            if (!cal.startDate || (cal.startDate && cal.endDate)) {
              cal.startDate = cellDate;
              cal.endDate = null;
              widget.refs.startInput.value = fmtDateLabel(cellDate);
              widget.refs.endInput.value = 'Select date';
              widget.state.rentalDates = { start: cellDate, end: null };
              widget._updateRentalButtonState();
              render();
              return;
            }
            if (cellDate <= cal.startDate) {
              cal.startDate = cellDate;
              cal.endDate = null;
              widget.refs.startInput.value = fmtDateLabel(cellDate);
              widget.refs.endInput.value = 'Select date';
              widget.state.rentalDates = { start: cellDate, end: null };
              widget._updateRentalButtonState();
              render();
              return;
            }
            let endDate = cellDate;
            const rawDays = daysBetween(cal.startDate, endDate) + 1;
            if (rawDays > MAX_RENTAL_DAYS) {
              cal.startDate = null;
              cal.endDate = null;
              widget._resetRentalInputs();
              render();
              return;
            }
            if (widget.state.minRentalDays && rawDays < widget.state.minRentalDays) {
              endDate = new Date(cal.startDate);
              endDate.setDate(endDate.getDate() + widget.state.minRentalDays - 1);
            }
            cal.endDate = endDate;
            widget.refs.startInput.value = fmtDateLabel(cal.startDate);
            widget.refs.endInput.value = fmtDateLabel(endDate);
            widget.state.rentalDates = { start: cal.startDate, end: endDate };
            widget._calcRentalPricing();
            widget._updateRentalButtonState();
            render();
            setTimeout(() => cal.close(), 200);
          });

          cell.addEventListener('mouseenter', () => {
            if (!cal.startDate || cal.endDate || disabled) return;
            if (sameDay(cal.hoverDate, cellDate)) return;
            cal.hoverDate = cellDate;
            render();
          });

          grid.appendChild(cell);
        }

        wrap.appendChild(grid);
        return wrap;
      };

      const render = () => {
        const isMobile = window.innerWidth <= 640;
        const monthsToShow = isMobile ? 1 : 2;
        container.innerHTML = '';
        const monthsWrap = el('div', { className: 'wuva-cal-months' });
        for (let i = 0; i < monthsToShow; i++) {
          const m = (cal.viewMonth + i) % 12;
          const y = cal.viewYear + Math.floor((cal.viewMonth + i) / 12);
          monthsWrap.appendChild(buildMonth(m, y, i === 0, i === monthsToShow - 1));
        }
        container.appendChild(monthsWrap);
      };

      const reposition = () => {
        const rect = widget.refs.startInput.getBoundingClientRect();
        const isMobile = window.innerWidth <= 640;
        const top = rect.bottom + window.scrollY + 8;
        if (isMobile) {
          container.style.left = '8px';
          container.style.right = '8px';
          container.style.width = 'auto';
          container.style.top = `${top}px`;
        } else {
          container.style.left = `${rect.left + window.scrollX}px`;
          container.style.right = '';
          container.style.width = '';
          container.style.top = `${top}px`;
        }
      };

      const onOutside = (e) => {
        if (container.contains(e.target)) return;
        if (e.target === widget.refs.startInput || e.target === widget.refs.endInput) return;
        cal.close();
      };

      cal.open = () => {
        if (cal.isOpen) return;
        cal.isOpen = true;
        container.dataset.open = 'true';
        if (cal.startDate) {
          cal.viewYear = cal.startDate.getFullYear();
          cal.viewMonth = cal.startDate.getMonth();
        }
        render();
        reposition();
        setTimeout(() => document.addEventListener('click', onOutside), 0);
      };

      cal.close = () => {
        if (!cal.isOpen) return;
        cal.isOpen = false;
        container.dataset.open = 'false';
        cal.hoverDate = null;
        document.removeEventListener('click', onOutside);
      };

      cal.clear = () => {
        cal.startDate = null;
        cal.endDate = null;
        cal.hoverDate = null;
        if (cal.isOpen) render();
      };

      cal.set = (key, value) => {
        if (key === 'minDate') {
          cal.minDate = startOfDay(value instanceof Date ? value : new Date(value));
          const viewFirst = new Date(cal.viewYear, cal.viewMonth, 1);
          const minMonthFirst = new Date(cal.minDate.getFullYear(), cal.minDate.getMonth(), 1);
          if (viewFirst < minMonthFirst) {
            cal.viewYear = cal.minDate.getFullYear();
            cal.viewMonth = cal.minDate.getMonth();
          }
          if (cal.isOpen) render();
        }
      };

      cal.setDate = (dates, triggerChange) => {
        if (!dates || dates.length === 0) {
          cal.startDate = null;
          cal.endDate = null;
        } else {
          cal.startDate = startOfDay(dates[0] instanceof Date ? dates[0] : new Date(dates[0]));
          cal.endDate = dates.length >= 2
            ? startOfDay(dates[1] instanceof Date ? dates[1] : new Date(dates[1]))
            : null;
          cal.viewYear = cal.startDate.getFullYear();
          cal.viewMonth = cal.startDate.getMonth();
        }
        if (cal.isOpen) render();
        if (triggerChange && cal.startDate && cal.endDate) {
          widget.refs.startInput.value = fmtDateLabel(cal.startDate);
          widget.refs.endInput.value = fmtDateLabel(cal.endDate);
          widget.state.rentalDates = { start: cal.startDate, end: cal.endDate };
          widget._calcRentalPricing();
          widget._updateRentalButtonState();
        }
      };

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (cal.isOpen) {
            render();
            reposition();
          }
        }, 200);
      });

      this.state.calendarInstance = cal;
      this._resetRentalInputs();

      if (this.state.rentalQuickDays > 0) {
        this._selectQuickDuration(this.state.rentalQuickDays);
      }
    }

    _applyCalendarConstraints() {
      const cal = this.state.calendarInstance;
      if (!cal || !this.state.rentalConstraints) return;
      cal.set('minDate', new Date(this.state.rentalConstraints.startDate));
    }

    _resetRentalInputs() {
      if (this.refs.startInput) this.refs.startInput.value = '';
      if (this.refs.endInput) this.refs.endInput.value = '';
      this.refs.startInput?.setAttribute('placeholder', 'Select date');
      this.refs.endInput?.setAttribute('placeholder', 'Select date');
      this.state.rentalDates = { start: null, end: null };
      if (this.refs.rentalBreakdown) this.refs.rentalBreakdown.style.display = 'none';
      this._updateRentalButtonState();
    }

    _calcRentalPricing() {
      const { start, end } = this.state.rentalDates;
      if (!start || !end || !this.state.rentalPricing) return;

      const days = Math.ceil((end - start) / 86400000) + 1;
      if (days <= 0) return;

      const rp = this.state.rentalPricing;
      const total = rp.fixedCosts + (rp.dayRate * days);
      const effectiveDailyRate = total / days;

      if (this.refs.rentalBreakdown) this.refs.rentalBreakdown.style.display = '';
      if (this.refs.rentalPeriod) this.refs.rentalPeriod.textContent = `${days} day${days !== 1 ? 's' : ''}`;
      if (this.refs.dailyRate) this.refs.dailyRate.textContent = `${this._fmtPounds(effectiveDailyRate)}/day`;
      if (this.refs.rentalTotal) this.refs.rentalTotal.textContent = this._fmtPounds(total);

      if (this.refs.rentalAtcBtn && !this._isCheckoutBlocked()) {
        this.refs.rentalAtcBtn.textContent = `Rent for ${this._fmtPounds(total)}`;
      }

      this._updateVatSaving();
    }

    _updateRentalButtonState() {
      if (!this.refs.rentalAtcBtn) return;
      const { start, end } = this.state.rentalDates;
      const hasDate = !!(start && end);
      const blocked = this._isCheckoutBlocked();

      this.refs.rentalAtcBtn.disabled = !hasDate || blocked;

      if (blocked) {
        this.refs.rentalAtcBtn.textContent = 'Fill in VAT declaration to rent';
      } else if (!hasDate) {
        this.refs.rentalAtcBtn.textContent = 'Select dates to rent';
      } else {
        this._calcRentalPricing();
      }
    }

    /* ======================================================================
       RENTAL: ADD TO CART
       ====================================================================== */

    async _handleRentalAddToCart() {
      if (this._isCheckoutBlocked()) return;

      const { start, end } = this.state.rentalDates;
      const rp = this.state.rentalPricing;
      if (!start || !end || !rp?.hireFeeInternalId || !rp?.dayRateInternalId) return;

      const btn = this.refs.rentalAtcBtn;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      // Flush any pending debounced cart attribute write before ATC
      if (this._syncDebounceTimer) {
        clearTimeout(this._syncDebounceTimer);
        this._syncDebounceTimer = null;
        if (this.state.vatRelief && this.state.vatFormValid) {
          this._cartAttrPromise = this._setCartAttributes();
        }
      }
      await this._cartAttrPromise;

      const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      const days = Math.ceil((end - start) / 86400000) + 1;

      const useVatFree = this.state.vatRelief && this.state.vatFormValid
        && rp.vatFreeHireFeeInternalId && rp.vatFreeHireFeeInternalId !== 'UNSET'
        && rp.vatFreeDayRateInternalId && rp.vatFreeDayRateInternalId !== 'UNSET';

      const hireFeeGid = useVatFree ? rp.vatFreeHireFeeInternalId : rp.hireFeeInternalId;
      const dayRateGid = useVatFree ? rp.vatFreeDayRateInternalId : rp.dayRateInternalId;
      const hireFeeId = hireFeeGid.split('/').pop();
      const dayRateId = dayRateGid.split('/').pop();

      const toGid = (id) => `gid://shopify/ProductVariant/${id}`;
      const bundleSpec = {
        startDate: startStr,
        hireFeeId: toGid(hireFeeId),
        components: [{ variantId: toGid(dayRateId), quantity: days }],
      };

      // V3.5: Added _client_id and _merchant alongside _revenue_model
      const props = {
        _revenue_model: 'rental',
        _merchant: MERCHANT_NAME,
        _startDate: startStr,
        _bundleSpec: JSON.stringify(bundleSpec),
        Days: String(days),
        _wuvaRentalComponents: JSON.stringify([{
          hire_fee_internal_id: rp.hireFeeInternalId,
          day_rate_internal_id: rp.dayRateInternalId,
          vat_free_hire_fee_internal_id: rp.vatFreeHireFeeInternalId || 'UNSET',
          vat_free_day_rate_internal_id: rp.vatFreeDayRateInternalId || 'UNSET',
          quantity: days,
        }]),
      };
      if (this.config.clientId) {
        props._client_id = this.config.clientId;
      }

      const cartItems = [
        { id: parseInt(hireFeeId), quantity: 1, properties: props },
        { id: parseInt(dayRateId), quantity: days },
      ];

      const addUrl = (window.Theme?.routes?.cart_add_url) || '/cart/add.js';
      const payload = JSON.stringify({ items: cartItems });
      const maxRetries = 3;

      try {
        let resp;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          resp = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: payload,
          });
          if (resp.status !== 429 || attempt === maxRetries) break;
          await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
        }

        if (resp.ok) {
          btn.textContent = 'Added ✓';
          this._dispatchCartEvent();
          setTimeout(() => {
            btn.disabled = false;
            this._updateRentalButtonState();
            this._calcRentalPricing();
          }, 1500);
        } else {
          const err = await resp.json().catch(() => ({}));
          console.error('[Wuva] Rental ATC API error:', resp.status, err);
          this._showError(err.message || err.description || 'Failed to add rental to cart');
          btn.disabled = false;
          this._calcRentalPricing();
        }
      } catch (e) {
        console.error('[Wuva] Rental ATC error:', e);
        this._showError('Network error — please try again');
        btn.disabled = false;
        this._calcRentalPricing();
      }
    }

    _dispatchCartEvent() {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, data: { source: 'wuva-widget' } },
          }));
          if (window.ThemeEvents?.cartUpdate) {
            document.dispatchEvent(new CustomEvent(window.ThemeEvents.cartUpdate, {
              bubbles: true,
              detail: { resource: cart, data: { source: 'wuva-widget' } },
            }));
          }
        })
        .catch(() => {});
    }

    _showError(msg) {
      if (!this.refs.error) return;
      this.refs.error.textContent = msg;
      this.refs.error.dataset.visible = 'true';
      setTimeout(() => {
        if (this.refs.error) this.refs.error.dataset.visible = 'false';
      }, 5000);
    }

    /* ======================================================================
       EVENT BINDING (Horizon integration)
       ====================================================================== */

    _bindEvents() {
      const section = this.container.closest('.shopify-section');
      if (!section) return;

      section.addEventListener('variant:update', (e) => this._onVariantUpdate(e));
      section.addEventListener('variant:selected', (e) => this._onVariantSelected(e));

      if (window.ThemeEvents) {
        if (window.ThemeEvents.variantUpdate) {
          section.addEventListener(window.ThemeEvents.variantUpdate, (e) => this._onVariantUpdate(e));
        }
        if (window.ThemeEvents.variantSelected) {
          section.addEventListener(window.ThemeEvents.variantSelected, (e) => this._onVariantSelected(e));
        }
      }

      this._interceptFormSubmit();
    }

    _onVariantSelected() {}

    _onVariantUpdate(event) {
      const resource = event.detail?.resource;
      if (!resource?.id) return;

      const newVariant = this._findVariant(resource.id);
      if (!newVariant) {
        this.variant = {
          id: resource.id,
          price: resource.price,
          available: resource.available,
          sku: resource.sku || '',
          externalVariantId: '',
          vatReliefEligible: false,
          vatReliefPairId: '',
          ...newVariant,
        };
      } else {
        this.variant = newVariant;
      }

      if (resource.price !== undefined) {
        this.variant.price = resource.price;
      }

      this._recalcPlanPrices();
      this._populatePlanGrid();
      this._updateSubscribeTabVisibility();

      this._updateAllPrices();
      this._checkRentalAvailability();

      if (this.state.calendarInstance) {
        this.state.calendarInstance.clear();
        this._resetRentalInputs();
      }

      this.state.rentalQuickDays = 0;
      if (this.refs.durationRow) {
        this.refs.durationRow.querySelectorAll('.wuva-duration-btn').forEach(btn => {
          btn.setAttribute('aria-checked', 'false');
        });
      }

      this._updateVatToggle();
      this._updateCheckoutBlocked();
    }

    _updateVatToggle() {
      if (!this.refs.vatBanner) return;
      if (this.variant?.vatReliefEligible) {
        this.refs.vatSection.style.display = '';
      } else {
        this.refs.vatSection.style.display = 'none';
        this.state.vatRelief = false;
        if (this.refs.vatToggle) this.refs.vatToggle.dataset.on = 'false';
        if (this.refs.vatBanner) this.refs.vatBanner.dataset.active = 'false';
        if (this.refs.vatFormWrap) this.refs.vatFormWrap.dataset.open = 'false';
        this._updateAllPrices();
      }
    }

    /* ======================================================================
       FETCH ACCURATE PLAN PRICES
       ====================================================================== */

    async _fetchAccuratePlanPrices() {
      try {
        const resp = await fetch(`/products/${this.config.productHandle}.js`);
        const product = await resp.json();
        const variant = product.variants.find(v => v.id == this.variant?.id);
        if (variant?.selling_plan_allocations) {
          variant.selling_plan_allocations.forEach(alloc => {
            this.planPrices[alloc.selling_plan_id] = alloc.per_delivery_price;
          });
          this._updateAllPrices();
        }
      } catch (e) {
        // Silently fail — calculated prices still work
      }
    }
  }

  /* ========================================================================
     5. INIT
     ======================================================================== */

  function init() {
    document.querySelectorAll('.wuva2-widget[data-section-id]').forEach(container => {
      const sectionId = container.dataset.sectionId;
      if (sectionId && !container.dataset.wuva2Init) {
        container.dataset.wuva2Init = 'true';
        new WuvaWidget(sectionId);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();